import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent,
  IonList, IonItem, IonLabel, IonCheckbox, IonSearchbar, IonIcon, IonSpinner,
} from "@ionic/react";
import { closeOutline, checkmarkOutline } from "ionicons/icons";
import { NoResults } from "@/components/NotificationsDrawer";
import { fetchBusinessEmployees, invalidateBusinessEmployees } from "@/components/SavedEmployees";
import { APP_LIVE_EVENT } from "@/utils/appLive";

// The pay stub modals' "Select an existing employee" (Pro/Enterprise): a row
// flush at the foot of the employee card that opens a picker the size of the
// pay stub modal, designed like the Assign picker: the X and the title with a
// green check at the end, a search bar, an Employees sub-bar over checkbox
// rows. One row checks at a time; the check hands that employee to onApply
// and closes, the X closes without a pick. Renders nothing for guests or
// plans without the feature.
// `country` limits the list to the employees paid on that stub (us, ca, uk).
export default function SelectExistingEmployee({ onApply, label = "Select an existing employee", country = "us" }) {
  const [state, setState] = useState({ hasAccess: false, employees: [] });
  const [open, setOpen] = useState(false);
  // the modal mounts fresh per open (the app overlay rule) but outlives
  // `open` so the close animates
  const [render, setRender] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null); // the checked employee's id
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    const data = await fetchBusinessEmployees(force);
    setState(data);
    setLoading(false);
  }, []);
  useEffect(() => {
    load();
    const on = () => { invalidateBusinessEmployees(); load(true); };
    window.addEventListener(APP_LIVE_EVENT, on);
    return () => window.removeEventListener(APP_LIVE_EVENT, on);
  }, [load]);

  const show = () => { setQuery(""); setPicked(null); setRender(true); setOpen(true); load(true); };
  const close = () => setOpen(false);
  const toggle = (emp) => setPicked((p) => (p === emp.id ? null : emp.id));
  const confirm = () => {
    const emp = state.employees.find((e) => e.id === picked);
    if (!emp) return;
    onApply(emp);
    close();
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const mine = state.employees.filter((e) => (e.country || "us") === country);
    if (!q) return mine;
    return mine.filter((e) => [e.name, e.employeeId, e.city, e.state].some((v) => (v || "").toLowerCase().includes(q)));
  }, [state.employees, query, country]);

  if (!state.hasAccess) return null;

  return (
    <>
      <IonItem button detail lines="none" className="card-foot-row ion-text-center" onClick={show}>
        <IonLabel color="primary">
          <span className="card-foot-point" aria-hidden="true">👉</span>{label}
        </IonLabel>
      </IonItem>
      {render && (
        <IonModal isOpen={open} onDidDismiss={() => { setRender(false); setOpen(false); }} className="admin-detail-modal admin-picker-modal employee-picker-modal">
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={close} aria-label="Close"><IonIcon icon={closeOutline} slot="icon-only" /></IonButton>
              </IonButtons>
              <IonTitle>Select an employee</IonTitle>
              <IonButtons slot="end">
                <IonButton fill="clear" shape="round" onClick={confirm} disabled={!picked} title="Done" aria-label="Done">
                  <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1rem", color: "var(--ion-color-success)" }}>
                    <IonIcon icon={checkmarkOutline} className="bold-icon" style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                  </span>
                </IonButton>
              </IonButtons>
            </IonToolbar>
            <IonToolbar>
              <IonSearchbar value={query} onIonInput={(e) => setQuery(e.detail.value || "")} placeholder="Search" debounce={120} showClearButton="never" />
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {loading && state.employees.length === 0 ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IonSpinner name="crescent" /></div>
            ) : filtered.length > 0 ? (
              <>
                <div className="admin-search-subbar">
                  <span className="admin-search-subbar-label">Employees</span>
                </div>
                <IonList lines="full">
                  {filtered.map((e) => (
                    <IonItem key={e.id} button detail={false} onClick={() => toggle(e)}>
                      <IonCheckbox slot="start" checked={picked === e.id} onClick={(ev) => ev.stopPropagation()} onIonChange={() => toggle(e)} aria-label={`Select ${e.name}`} />
                      <IonLabel>{e.name}</IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </>
            ) : (
              <NoResults text={query.trim() ? `No results found for "${query.trim()}"` : `No ${country === "ca" ? "Canadian" : country === "uk" ? "UK" : "US"} employees yet`} />
            )}
          </IonContent>
        </IonModal>
      )}
    </>
  );
}
